import { PlaygroundConfigurationForm } from "@/components/PlaygroundConfigurationForm";
import { expandDeviceStateKey, type DeviceStateStatus } from "@/lib/device-operations";
import { readDeviceSetup, writeDeviceSetup, resolveSetupAssets, runDeviceSetup, type DeviceSetup } from "@/lib/device-setup";
import type { Limrun } from '@limrun/api';
import { AlertCircle, Smartphone } from "lucide-react";
import { useEffect, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';

import { orgRequestOptions } from "@/client/director/org";
import { usePlaygroundConfiguration } from "@/hooks/usePlaygroundConfiguration";
import { APIError } from "@limrun/api/core/error";
import { toastInstanceCreationError } from '@/components/instance-creation-error';
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from '@/components/ui/use-toast';
import { Loader, X } from "lucide-react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  AnalyticsEvents,
  captureAnalyticsEvent,
  type StreamDeviceClass,
} from "@/analytics";
import {
  transformUrl,
  useAuth,
} from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization.tsx";
import { DeviceSession } from "@/components/session/DeviceSession";
import { buildRuntimeTokenUrl } from "@/hooks/useNetworkInspection";
import { useOrganizationScopes } from "@/lib/scopes";

import { platformFromInstanceId } from "@/lib/instance-platform.ts";
import { parseIOSModel, type IOSModel } from "@/lib/ios-model";
import { formatErrorMessage } from '@/utils/format';

import { hasPlaygroundConfiguration, parsePlaygroundConfiguration, playgroundRequest, type PlaygroundConfiguration } from "@/lib/playground-configuration";

type AndroidModel = NonNullable<
  NonNullable<Limrun.AndroidInstanceCreateParams["spec"]>["model"]
>;

export default function StreamPage() {
  const { instanceId } = useParams<{ instanceId?: string }>();
  const location = useLocation();
  // Only the create-to-stream transition retains the form's mounted launch runner.
  const pageKey: string = instanceId ? location.state?.playgroundKey ?? location.key : location.key;
  const platform = instanceId ? platformFromInstanceId(instanceId) : undefined;
  if (platform === "xcode" || platform === "gradle") {
    return <Navigate to={`/builds/${instanceId}`} replace />;
  }
  return <StreamPageInner key={pageKey} pageKey={pageKey} instanceId={instanceId} />;
}

function StreamPageInner({ instanceId, pageKey }: { instanceId?: string; pageKey: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linked = usePlaygroundConfiguration(searchParams, !instanceId && hasPlaygroundConfiguration(searchParams));
  const [instance, setInstance] = useState<Limrun.AndroidInstance | Limrun.IosInstance | "loading" | undefined>(() => instanceId ? "loading" : undefined);
  const [organization] = useOrganization();
  const auth = useAuth();

  // State for managing instance creation and connection
  const [isCreating, setIsCreating] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const creating = useRef(false);
  const launchRun = useRef(0);
  const [launchMessage, setLaunchMessage] = useState<string | null>(null);
  const [pendingSetup, setPendingSetup] = useState(() => readDeviceSetup(instanceId));
  const [launchError, setLaunchError] = useState<string | undefined>(() =>
    pendingSetup ? "Setup was interrupted. Resume setup to finish installing and launching your apps." : undefined);
  const setupRunning = useRef(false);
  const [streamDuringInstall, setStreamDuringInstall] = useState(false);
  const [instanceOrganizationId, setInstanceOrganizationId] = useState<string | undefined>(pendingSetup?.organizationId);
  useEffect(() => () => { launchRun.current += 1; }, []);
  const streamContainerRef = useRef<HTMLDivElement>(null);
  const connectedInstanceIdRef = useRef<string | undefined>(undefined);
  const [selectedIOSModel, setSelectedIOSModel] = useState<IOSModel>(
    pendingSetup?.configuration.platform === 'ios' ? pendingSetup.configuration.model : 'iphone',
  );
  const [selectedAndroidModel, setSelectedAndroidModel] = useState<AndroidModel>(pendingSetup?.configuration.platform === 'android' ? pendingSetup.configuration.model : 'phone');
  const [selectedPlatform, setSelectedPlatform] = useState<"ios" | "android">("android");
  const isTabletSelected = selectedPlatform === 'android' && selectedAndroidModel === 'tablet';
  const streamIOSModel = parseIOSModel(searchParams.get("model"));
  const streamWhenUrlAvailable = searchParams.get("streamWhenUrlAvailable") === "true" || streamDuringInstall;
  const currentInstance = instance && instance !== "loading" ? instance : undefined;
  const snapshotRestartRequired = !!pendingSetup?.snapshotPending && !!currentInstance &&
    (currentInstance.status as typeof currentInstance.status & { deviceState?: DeviceStateStatus }).deviceState?.restore.downloadUrl === undefined;
  const isCurrentIOS = currentInstance?.metadata.id.startsWith("ios_") || (!currentInstance && (instanceId ? instanceId.startsWith("ios_") : selectedPlatform === "ios"));
  // Starting or streaming an instance needs all on its kind; a role that
  // only reads sees the playground and the instance records, nothing more.
  // Controls wait on can, which stays false until the seat is known;
  // notices use denied, so nothing is said until it is.
  const scopeOrganization = instanceOrganizationId
    ? linked.organizations.find(org => org.tid === instanceOrganizationId) ?? "loading"
    : organization;
  const { can, denied } = useOrganizationScopes(scopeOrganization);
  const canStartInstance = can(isCurrentIOS ? "ios" : "android", "all");
  const viewOnlyRole = denied(isCurrentIOS ? "ios" : "android", "all");
  const effectiveIOSModel = currentInstance?.metadata.id.startsWith("ios_")
    ? streamIOSModel ?? selectedIOSModel
    : selectedIOSModel;
  const isDuo = isCurrentIOS && effectiveIOSModel === "iphone-duo";
  const showFrame = isCurrentIOS
    ? effectiveIOSModel === "iphone" || effectiveIOSModel === "iphone-duo"
    : !isTabletSelected && searchParams.get("model") !== "tablet";
  const streamUrl = currentInstance?.status?.endpointWebSocketUrl;
  const canStartStreaming = !!streamUrl && currentInstance?.status.state !== "terminated" &&
    (streamWhenUrlAvailable || currentInstance?.status?.state === "ready");
  const deviceInfoUrl = isCurrentIOS && canStartStreaming && canStartInstance &&
    currentInstance?.status.apiUrl && currentInstance.status.token
    ? buildRuntimeTokenUrl(transformUrl(currentInstance.status.apiUrl)!, "/deviceInfo", currentInstance.status.token)
    : undefined;
  const { data: deviceInfo } = useSWR<{ model: string; name: string }>(
    deviceInfoUrl ? ["deviceInfo", currentInstance?.metadata.id, deviceInfoUrl] : null,
    async () => {
      const response = await fetch(deviceInfoUrl!);
      if (!response.ok) throw new Error(`Failed to load device info: HTTP ${response.status}`);
      return response.json();
    },
    { revalidateOnFocus: false, errorRetryCount: 3 },
  );
  const deviceTitle = deviceInfo?.model?.trim() || deviceInfo?.name?.trim() ||
    currentInstance?.metadata.displayName ||
    (instanceId?.startsWith("ios_") || isCurrentIOS ? "iOS device" : "Android device");

  useEffect(() => {
    const container = streamContainerRef.current;
    const currentInstanceId = currentInstance?.metadata.id;
    if (!container || !canStartStreaming || !currentInstanceId) return;

    const captureConnectedStream = (event: Event) => {
      if (!(event.target instanceof HTMLVideoElement)) return;
      if (connectedInstanceIdRef.current === currentInstanceId) return;
      connectedInstanceIdRef.current = currentInstanceId;
      captureAnalyticsEvent(AnalyticsEvents.STREAM_CONNECTED, {
        platform: currentInstanceId.startsWith("ios_") ? "ios" : "android",
        source: "console",
      });
    };
    // Media `playing` does not bubble. A native capture listener on the
    // container observes the video rendered inside RemoteControl without
    // requiring RemoteControl to expose a separate readiness callback.
    container.addEventListener("playing", captureConnectedStream, true);
    return () => {
      container.removeEventListener("playing", captureConnectedStream, true);
    };
  }, [canStartStreaming, currentInstance?.metadata.id]);

  const checkpointSetup = (id: string, setup?: DeviceSetup) => {
    setPendingSetup(setup);
    if (!writeDeviceSetup(id, setup)) {
      toast({ title: "Keep this tab open", description: "Your browser could not save setup progress. Resuming after a refresh is unavailable.", variant: "destructive" });
    }
  };

  const finishSetup = async (device: Limrun.IosInstance | Limrun.AndroidInstance, setup: DeviceSetup, assets?: Limrun.Asset[], run = ++launchRun.current) => {
    const active = () => launchRun.current === run;
    setupRunning.current = true;
    setLaunchError(undefined);
    const progress = (text: string | null) => {
      if (active()) setLaunchMessage(text);
    };
    try {
      if (!device.status.endpointWebSocketUrl || !device.status.token) throw new Error("Device is missing its connection details.");
      progress("Preparing apps");
      const resolved = assets ?? await resolveSetupAssets(auth.regionClient, setup);
      if (!active()) return;
      await runDeviceSetup(auth.regionClient, device, setup, resolved, progress, next => checkpointSetup(device.metadata.id, next), active);
      if (active()) checkpointSetup(device.metadata.id);
    } catch (error) {
      if (active()) {
        setLaunchMessage(null);
        setLaunchError(error instanceof Error ? error.message : String(error));
      }
    } finally {
      if (active()) setupRunning.current = false;
    }
  };

  const handleCreateAndConnect = async (configuration: PlaygroundConfiguration, launchOptions?: { replaceSnapshot: boolean }) => {
    if (instanceId || instance || creating.current || organization === "loading" || (linked.attributeViewer && (linked.loading || !linked.viewerEmail))) return;
    if (configuration.deviceState?.includes("{user}") && !linked.viewerEmail) {
      toast({ title: "Could not load your email address", description: "Reload the page before starting a device with a per-user state key.", variant: "destructive" });
      return;
    }
    creating.current = true;
    const run = ++launchRun.current;
    const active = () => launchRun.current === run;
    setIsCreating(true);
    setLaunchError(undefined);
    setSelectedPlatform(configuration.platform);
    if (configuration.platform === "ios") setSelectedIOSModel(configuration.model);
    else setSelectedAndroidModel(configuration.model);
    setStreamDuringInstall(true);
    setInstanceOrganizationId(organization.tid);
    const options = orgRequestOptions(organization.tid);
    let createRequested = false;
    let snapshotDeleted = false;
    const setup: DeviceSetup = { configuration, organizationId: organization.tid, nextAsset: 0, snapshotPending: configuration.platform === "ios" && !!configuration.deviceState };
    try {
      // Validate every selected app before allocating a billable device.
      const assets = await resolveSetupAssets(auth.regionClient, setup);
      if (!active()) return;
      const request = playgroundRequest(configuration);
      const deviceState = configuration.platform === "ios" && configuration.deviceState
        ? { key: expandDeviceStateKey(configuration.deviceState, linked.viewerEmail ?? ""), restoreMode: "client" as const } : undefined;
      if (launchOptions?.replaceSnapshot && deviceState) {
        const { error, response } = await auth.client.DELETE("/v1/organizations/{organizationId}/ios_device_states/{key}", {
          params: { path: { organizationId: organization.tid, key: deviceState.key } },
        });
        if (error && response.status !== 404) throw new Error(`Could not replace snapshot: ${error.message}`);
        snapshotDeleted = true;
        void mutate(["ios-snapshot", organization.tid, deviceState.key], null, { revalidate: false });
        void mutate(key => Array.isArray(key) && key[0] === "ios-snapshots" && key[1] === organization.tid);
        if (!active()) return;
      }
      if (request.platform === "ios" && deviceState) {
        request.params.spec = { ...request.params.spec, deviceState } as Limrun.IosInstanceCreateParams["spec"];
        // Snapshot restoration must not overwrite an already-running session reused by labels.
        request.params.metadata = { ...request.params.metadata, labels: {
          ...request.params.metadata?.labels, "preview-session": `preview-${crypto.randomUUID()}`,
        } };
      }
      if (linked.attributeViewer && linked.viewerEmail) {
        request.params.metadata = { ...request.params.metadata, labels: {
          ...request.params.metadata?.labels, "user-email": linked.viewerEmail,
        } };
      }
      const streamDeviceClass: StreamDeviceClass = configuration.model === "watch" ? "watch"
        : configuration.model === "ipad" || configuration.model === "tablet" ? "tablet" : "phone";
      captureAnalyticsEvent(AnalyticsEvents.INSTANCE_CREATE_REQUESTED, {
        platform: configuration.platform, device_class: streamDeviceClass,
        has_initial_assets: configuration.assets.length > 0, source: "console",
      });
      createRequested = true;
      const result = request.platform === "ios"
        ? await auth.regionClient.iosInstances.create(request.params, options)
        : await auth.regionClient.androidInstances.create(request.params, options);
      // Record the continuation even if navigation unmounted this page during creation.
      if (configuration.assets.length || configuration.openUrl || setup.snapshotPending) {
        if (active()) checkpointSetup(result.metadata.id, setup);
        else writeDeviceSetup(result.metadata.id, setup);
      }
      if (!active()) return;
      setInstance(result);
      setIsCreating(false);
      navigate(`/stream/${result.metadata.id}?${new URLSearchParams({ model: configuration.model, streamWhenUrlAvailable: "true" })}`, { replace: true, state: { playgroundKey: pageKey } });
      void mutate(request.platform === "ios" ? "iosInstances" : "androidInstances");
      await finishSetup(result, setup, assets, run);
    } catch (error: unknown) {
      if (!active()) return;
      const bodyMessage = error instanceof APIError ? (error.error as Record<string, unknown>)?.message : undefined;
      const message = typeof bodyMessage === "string" ? bodyMessage : error instanceof Error ? error.message : String(error);
      setLaunchError(snapshotDeleted
        ? `The previous snapshot was deleted, but the device could not start. ${message}`
        : error instanceof APIError && error.status === 404
          ? `${message} You may not be a member of the organization that owns this asset.` : message);
      if (createRequested) toastInstanceCreationError(error);
    } finally {
      if (active()) {
        creating.current = false;
        setIsCreating(false);
      }
    }
  };

  const handleStopInstance = async () => {
    if (!instance || !instanceId || isStopping) return;
    const idToDelete = instance === "loading" ? instanceId : instance.metadata.id;
    const options = orgRequestOptions(instanceOrganizationId ?? (organization === "loading" ? undefined : organization.tid));

    launchRun.current += 1;
    setupRunning.current = false;
    checkpointSetup(idToDelete);
    setIsStopping(true);
    setLaunchError(undefined);
    setLaunchMessage("Stopping device...");
    try {
      if (idToDelete.startsWith('ios_')) {
        await auth.regionClient.iosInstances.delete(idToDelete, options);
        await mutate('iosInstances');
      } else {
        await auth.regionClient.androidInstances.delete(idToDelete, options);
        await mutate('androidInstances');
      }
      creating.current = false;
      setInstance(undefined);
      setIsCreating(false);
      setLaunchMessage(null);
      setLaunchError(undefined);
      setStreamDuringInstall(false);
      navigate('/stream');
    } catch (error: unknown) {
      setLaunchMessage(null);
      setLaunchError(`Could not stop device: ${formatErrorMessage((error as { message?: string }).message ?? String(error))}. Launch steps were cancelled; try Stop again.`);
      toast({
        title: "Uh oh!",
        description: formatErrorMessage((error as { message?: string }).message ?? String(error)),
        variant: "destructive",
      });
      return;
    } finally {
      setIsStopping(false);
    }
  };

  useEffect(() => {
    if (!instanceId || isStopping || (currentInstance && ["ready", "terminated"].includes(currentInstance.status.state))) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let retryDelay = 1000;
    const fetchInstance = async () => {
      try {
        const device = await auth.regionClient[instanceId.startsWith('ios_') ? 'iosInstances' : 'androidInstances'].get(instanceId, orgRequestOptions(instanceOrganizationId));
        if (cancelled) return;
        if (device.status.state === "terminated") {
          setInstance(device);
          checkpointSetup(instanceId);
          setLaunchError("This device has stopped. Open Playground to start another device.");
          return;
        }
        if (device.status.state === "ready") {
          setInstance(device);
          const snapshotUnavailable = pendingSetup?.snapshotPending &&
            (device.status as typeof device.status & { deviceState?: DeviceStateStatus }).deviceState?.restore.downloadUrl === undefined;
          setLaunchError(snapshotUnavailable
            ? "Snapshot restoration was interrupted. Stop this device and reopen the preview link to start again."
            : pendingSetup ? "Setup was interrupted. Resume setup to finish installing and launching your apps." : undefined);
          return;
        }
        if (streamWhenUrlAvailable && device.status.endpointWebSocketUrl &&
          (device.status.state !== currentInstance?.status.state || device.status.endpointWebSocketUrl !== currentInstance.status.endpointWebSocketUrl)) {
          setInstance(device);
        }
      } catch (error) {
        if (cancelled) return;
        // The read model can lag creation. Retry without assuming a fixed grace period.
        if (!(error instanceof APIError && error.status === 404)) {
          setLaunchError(`Could not connect to device: ${formatErrorMessage(error instanceof Error ? error.message : String(error))}`);
          if (error instanceof APIError && (error.status === 401 || error.status === 403)) return;
        }
      }
      timer = setTimeout(fetchInstance, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 10_000);
    };
    void fetchInstance();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [instanceId, instance, auth.regionClient, streamWhenUrlAvailable, instanceOrganizationId, isStopping, pendingSetup]);

  if (instance) {
    const ready = currentInstance?.status.state === "ready";
    return (
      <AppShell title={deviceTitle} viewport>
        <DeviceSession
          title={deviceTitle}
          message={launchMessage}
          error={launchError}
          errorAction={pendingSetup && !snapshotRestartRequired && <Button size="sm"
            disabled={!currentInstance || !ready || isStopping || !canStartInstance}
            onClick={() => { if (currentInstance && canStartInstance && !setupRunning.current) void finishSetup(currentInstance, pendingSetup); }}>
            Resume setup
          </Button>}
          status={
            <>
              <span className={`size-1.5 rounded-full ${launchError ? "bg-destructive" : ready && !launchMessage ? "bg-success" : "bg-warning"}`} />
              {launchError ? "Failed" : launchMessage || !ready ? "Preparing" : "Running"}
            </>
          }
          actions={
            <Button size="sm" variant="danger-ghost"
              disabled={!instanceId || !canStartInstance || isStopping}
              onClick={handleStopInstance}>
              <X />Stop
            </Button>
          }
          ready={ready}
          canUseTools={canStartInstance}
          showFrame={showFrame}
          isDuo={isDuo}
          streamContainerRef={streamContainerRef}
          connection={canStartStreaming && currentInstance?.status.token ? {
            instanceId: currentInstance.metadata.id,
            platform: isCurrentIOS ? "ios" : "android",
            url: transformUrl(streamUrl)!,
            token: currentInstance.status.token,
            apiUrl: currentInstance.status.apiUrl,
            adbWebSocketUrl: (currentInstance.status as { adbWebSocketUrl?: string }).adbWebSocketUrl,
            mcpUrl: (currentInstance.status as { mcpUrl?: string }).mcpUrl,
          } : undefined}
          fallback={viewOnlyRole ? (
            <div className="flex h-full items-center justify-center">
              <EmptyState icon={<Smartphone />} title="View-only access"
                description="Ask an admin for a member seat to control this device." />
            </div>
          ) : undefined}
        />
      </AppShell>
    );
  }

  if (linked.error) {
    return (
      <AppShell title="Playground" centered>
        <EmptyState icon={<AlertCircle />} title="Preview failed"
          description={linked.error} />
      </AppShell>
    );
  }
  if (linked.loading) {
    return (
      <AppShell title="Playground" centered>
        <div role="status"><EmptyState icon={<Loader className="animate-spin" />} title="Loading preview configuration" description="Resolving the app's organization..." /></div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Playground" width="form">
      <PageHeader
        title="Playground"
        description="Configure a device, run your app, and inspect it live."
      />
      {launchError && <p role="alert" className="text-sm text-destructive">{launchError}</p>}
      <PlaygroundConfigurationForm
        initialConfiguration={linked.configuration ?? parsePlaygroundConfiguration(new URLSearchParams())}
        defaultToSnapshotOS={!searchParams.get("osVersion")}
        isCreating={isCreating}
        onStart={handleCreateAndConnect}
      />
    </AppShell>
  );
}
